"use client";

import { useTranslations } from "next-intl";
import { AppInput, AppInputPhone } from "@/components/design/forms";
import { ContactInput } from "@/components/design/shared/ContactInput";
import { CountrySearchInput } from "@/components/design/shared/CountrySearchInput";
import type { ParticipantFormData } from "./registrationFlowTypes";

export interface ParticipantPartner2ContactGridProps {
    participant: ParticipantFormData;
    errors: Record<string, string>;
    isActive: boolean;
    partner2UiActive: boolean;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
}

export function ParticipantPartner2ContactGrid({
    participant,
    errors,
    isActive,
    partner2UiActive,
    onUpdate,
}: ParticipantPartner2ContactGridProps) {
    const t = useTranslations("strings.registrationFlow");
    return (
        <div className="grid min-w-0 w-full max-w-full grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-12 items-start">
                                                <div className="lg:col-span-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("emailLabel")}
                                                    </label>
                                                    <AppInput
                                                        type="email"
                                                        value={
                                                            participant.partner2Email ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2Email:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("placeholderEmailPartner")}
                                                        disabled={!partner2UiActive}
                                                    />
                                                    {isActive &&
                                                        errors.partner2Email && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2Email
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("phoneLabel")}
                                                    </label>
                                                    <AppInputPhone
                                                        value={
                                                            participant.partner2Phone ||
                                                            ""
                                                        }
                                                        country={participant.partner2Country}
                                                        onChange={(partner2Phone) =>
                                                            onUpdate({
                                                                partner2Phone,
                                                            })
                                                        }
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? errors.partner2Phone
                                                                    ? "border-fest-red text-fest-heading focus:border-fest-red"
                                                                    : "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("placeholderPhoneExample")}
                                                        disabled={!partner2UiActive}
                                                        ariaInvalid={isActive && !!errors.partner2Phone}
                                                    />
                                                    {isActive &&
                                                        errors.partner2Phone && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2Phone
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("countryLabel")}
                                                    </label>
                                                    <CountrySearchInput
                                                        value={
                                                            participant.partner2Country ||
                                                            ""
                                                        }
                                                        onChange={(partner2Country) =>
                                                            onUpdate({ partner2Country })
                                                        }
                                                        error={!!errors.partner2Country}
                                                        disabled={!partner2UiActive}
                                                        inputClassName={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? errors.partner2Country
                                                                    ? "border-fest-red text-fest-heading focus:border-fest-red"
                                                                    : "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("countryPlaceholderRu")}
                                                    />
                                                    {isActive &&
                                                        errors.partner2Country && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2Country
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("cityLabel")}
                                                    </label>
                                                    <AppInput
                                                        type="text"
                                                        value={
                                                            participant.partner2City ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2City:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? errors.partner2City
                                                                    ? "border-fest-red text-fest-heading focus:border-fest-red"
                                                                    : "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("cityPlaceholderMoscow")}
                                                        disabled={!partner2UiActive}
                                                    />
                                                    {isActive &&
                                                        errors.partner2City && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2City
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div
                                                    className={`col-span-1 min-w-0 max-w-full sm:col-span-1 lg:col-span-4 justify-self-start w-full max-w-[min(100%,22rem)] lg:max-w-none ${
                                                        !partner2UiActive
                                                            ? "opacity-60 pointer-events-none"
                                                            : ""
                                                    }`}
                                                >
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("contactLabel")}
                                                    </label>
                                                    <ContactInput
                                                        type={
                                                            participant.partner2ContactType ||
                                                            "telegram"
                                                        }
                                                        value={
                                                            participant.partner2ContactValue ||
                                                            ""
                                                        }
                                                        onTypeChange={(t) =>
                                                            onUpdate({
                                                                partner2ContactType: t,
                                                                partner2Instagram:
                                                                    t ===
                                                                    "instagram"
                                                                        ? participant.partner2ContactValue ||
                                                                          ""
                                                                        : "",
                                                            })
                                                        }
                                                        onValueChange={(v) =>
                                                            onUpdate({
                                                                partner2ContactValue: v,
                                                                partner2Instagram:
                                                                    participant.partner2ContactType ===
                                                                    "instagram"
                                                                        ? v
                                                                        : participant.partner2Instagram,
                                                            })
                                                        }
                                                        compact
                                                    />
                                                </div>
                                            </div>
    );
}
